import throttle from 'lodash/throttle';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import { useCreateAutoDebitRegistrationMutation } from 'apollo/queries/autogenerate/hooks';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import DatePicker from 'shared/modules/common/components/molecules/DatePicker';
import validator from 'shared/utils/validator';

type Data = {
  lastName: string;
  firstName: string;
  dateOfBirth: string;
};

type ADRProps = {
  lastName: string;
  firstName: string;
  dateOfBirth: string;
};

type Props = {
  /**
   * Invoked when getting kfrequest token is successful
   */
  onSuccess?: (kfrequestToken: string) => void;
  onFailure?: (error: Error) => void;
  data?: Data;
  onDataUpdate?: (data: Partial<Data>) => void;
};

const fieldNames: Array<keyof ADRProps> = [
  'lastName',
  'firstName',
  'dateOfBirth',
];

const AutoDebitRegistrationForm = (props: Props) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [createADR] = useCreateAutoDebitRegistrationMutation();

  const form = useFormWithValidations(props.data as Data);

  const submit = useCallback(
    throttle(
      async () => {
        try {
          const response = await createADR({
            variables: {
              lastName: form.getValues(fieldNames[0]) as string,
              firstName: form.getValues(fieldNames[1]) as string,
              dateOfBirth: `${form.getValues(fieldNames[2])}T00:00:00+09:00`,
            },
          });

          props.onSuccess?.(
            response.data?.createAutoDebitRegistration.kfrequest || ''
          );
        } catch (error) {
          props.onFailure?.(error);
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    []
  );

  return (
    <View
      style={style.root}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      <View>
        <Text style={style.textTitle}>お客様情報</Text>
        <TextInput
          label="姓(全角カタカナ・全角ローマ字)"
          placeholder="ヤマダ"
          value={form.getValues(fieldNames[0]) as string}
          onChangeText={(lastName) => {
            form.setValue(fieldNames[0], lastName);
            props.onDataUpdate?.({
              lastName,
            });
          }}
          onBlur={() => form.triggerValidation(fieldNames[0])}
          message={(form.errors[fieldNames[0]]?.message as string) || ''}
          state={form.errors[fieldNames[0]]?.message ? 'error' : undefined}
          style={style.textInput}
          id="adr-last-name-field"
        />
        <TextInput
          label="名(全角カタカナ・全角ローマ字)"
          placeholder="タロウ"
          value={form.getValues(fieldNames[1]) as string}
          onChangeText={(firstName) => {
            form.setValue(fieldNames[1], firstName);
            props.onDataUpdate?.({
              firstName,
            });
          }}
          onBlur={() => form.triggerValidation(fieldNames[1])}
          message={(form.errors[fieldNames[1]]?.message as string) || ''}
          state={form.errors[fieldNames[1]]?.message ? 'error' : undefined}
          style={style.textInput}
          id="adr-first-name-field"
        />
        <DatePicker
          label="生年月日"
          placeholder="YYYY-MM-DD"
          value={form.getValues(fieldNames[2]) as string}
          onSelect={(dateOfBirth) => {
            form.setValue(fieldNames[2], dateOfBirth);
            props.onDataUpdate?.({
              dateOfBirth,
            });
          }}
          onBlur={() => form.triggerValidation(fieldNames[2])}
          message={(form.errors[fieldNames[2]]?.message as string) || ''}
          state={form.errors[fieldNames[2]]?.message ? 'error' : undefined}
          selectButtonText="保存する"
          showAsDialog={isBigScreen}
          style={style.textInput}
          id="adr-birthday-picker"
        />
      </View>
      <View>
        <Button
          title="手続きを進める"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            isValid && submit();
          }}
          id="adr-submit-button"
        />
      </View>
    </View>
  );
};

const useFormWithValidations = (props: ADRProps) => {
  const form = useForm<ADRProps>({
    defaultValues: props,
  });

  useEffect(() => {
    fieldNames.forEach((name, index) =>
      form.register(
        { name },
        {
          validate:
            index === 2
              ? validator.getRules([
                  // Birthday
                  'mustNotBeEmptyOrSpace',
                  'mustBeValidDate',
                ])
              : validator.getRules([
                  // Last and First names
                  'mustNotBeEmptyOrSpace',
                  'mustBeFullWidthKatakanaAlphabetSpaces',
                ]),
        }
      )
    );
  }, [form.register]);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  textTitle: {
    fontWeight: 'bold',
    fontSize: 24,
    marginBottom: 32,
  },
  textInput: {
    marginBottom: 24,
  },
});

export default AutoDebitRegistrationForm;
